import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import { getBitrixDeal } from 'src/store/crosstable/selectors';
import { Actions } from 'src/store/requests/filter/actions';
import { Actions as ActionsView2d } from 'src/store/view-2d/actions';
import { Actions as ActionsFilteredData } from 'src/store/filter/filtered-data/actions';
import { Actions as SpinnerActions } from 'src/store/requests/spinner-views/actions';
import { Actions as AppActions } from 'src/store/app/actions';
import {
  getCrmData, getCurrentLang, getSelectedAppartment,
} from 'src/store/app/selectors';
import { useGetAuthData } from 'src/hooks';
import { getBitrixPlacementDeals, patchBitrixDeal } from 'src/api';
import { Actions as ActionsCross } from 'src/store/crosstable/actions';
import { IBitrixDeal, IBitrixDealPlacement } from 'src/store/crosstable/reducer';
import { DealButton } from 'src/components/ApartmentConnectDeal/components/Buttons/DealButton';
import { LinkedToDealButton } from 'src/components/ApartmentConnectDeal/components/Buttons/LinkedToDealButton';
import {
  DealDetailsWrapper,
} from 'src/components/ApartmentConnectDeal/components/DealDetailsWrapper/dealDetailsWrapper';
import { useBitrixDealHook } from 'src/components/ApartmentConnectDeal/hooks/useBitrixDealHook';
import {
  ApartmentSidebarUndo,
} from 'src/components/CrosstableContent/components/AppartmentSidebarCross/components/ApartmentSidebarUndo';
import {
  BlockedButton,
} from 'src/components/ApartmentConnectDeal/components/Buttons/CantBeUnLinkedButton';
import {
  useCrmBitrixDeal,
} from 'src/components/CrosstableContent/components/AppartmentSidebarCross/hooks/use-placement-deals';
import { IApartmentConnectDeal } from './index';

// A deal's placement arrives either as a nested object or as an IRI string
// ("/api/placements/{id}").
const extractPlacementId = (
  placement?: IBitrixDealPlacement | string | null,
): string | undefined => {
  if (!placement) return undefined;
  if (typeof placement === 'string') return placement.split('/').filter(Boolean).pop();
  return placement.id;
};

export const BitrixDeal: React.FC<IApartmentConnectDeal> = React.memo(({
  onLoading,
  placementDeal: placementDealProp,
  refreshPlacementDeal,
  updatePlacementDeal,
  unitId: unitIdProp,
}) => {
  const dispatch = useDispatch();
  const crmData = useSelector(getCrmData);
  const bitrixDeal = useSelector(getBitrixDeal);
  const currentLang = useSelector(getCurrentLang);
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const [currentDeals, setCurrentDeals] = useState<IBitrixDeal[]>([]);
  const [unlinkDealId, setUnlinkDealId] = useState<string>('');
  const [patchError, setPatchError] = useState<string | null>(null);
  const selectedApartmentId = useSelector(getSelectedAppartment);
  const ownPlacementDeal = useCrmBitrixDeal();
  // Managed mode: the sidebar owns the SINGLE placement fetch per selected unit
  // and shares its state via props — no second identical GET from here. The own
  // hook instance is the fallback for legacy standalone contexts.
  const isPlacementDealManaged = !!refreshPlacementDeal;
  const placementDeal = isPlacementDealManaged ? placementDealProp : ownPlacementDeal.placementDeal;
  const getPlacementDeals = refreshPlacementDeal ?? ownPlacementDeal.getPlacementDeals;
  const setPlacementDeal = updatePlacementDeal ?? ownPlacementDeal.setPlacementDeal;
  const {
    isUnitCanBeLinked, isUnitCannotBeLinkedByStatus, isDealHasCurrentUnit, undoProgress, setUndoProgress,
    isBitrixDealUnlinkBlocked, isBitrixDealAccessRestricted, bitrixDealAccessRestrictedMessage,
    isLinkingNotEnabledForUser, isLinkingBlockedByResponsibility,
  } = useBitrixDealHook(bitrixDeal, placementDeal);
  // The unit this instance is about: the sidebar keys off the store selection,
  // while hover popovers (Floors/3D) pass their own unit — the store selection
  // there is stale and everything (fetch, green state, gates) was computed
  // against the wrong unit.
  const unitId = unitIdProp ?? selectedApartmentId;
  const isDealHasCurrentUnitOnFloors = !!unitId && unitId === bitrixDeal?.placement?.id;
  // While the freshly selected unit's placement (and its @accessRules) is still
  // loading, placementDeal belongs to the PREVIOUS unit — the status block is
  // then unknown and the deal-side notice ("#N is linked…") flashed for a moment
  // before the status notice replaced it. Hold the action area until the loaded
  // placement matches the unit.
  const isPlacementReady = !!placementDeal && !!unitId
    && String(placementDeal.id) === String(unitId);

  useEffect(() => {
    // Self-fetch (non-managed contexts) runs only in CRM mode — crmData is the
    // gate, and the wrapper renders BitrixDeal only for the bitrix CRM.
    if (crmData && !isPlacementDealManaged) {
      getPlacementDeals(unitId);
    }
  }, [unitId, crmData, isPlacementDealManaged]);

  useEffect(() => {
    if (undoProgress === 100 && unlinkDealId.length) {
      // Fire exactly once: clear the pending id synchronously so a re-render
      // (e.g. placementDeal refresh) cannot re-trigger the PATCH.
      const dealId = unlinkDealId;
      setUnlinkDealId('');
      onLoading?.(true);
      handleRemoveDeal(dealId);
    }
  }, [undoProgress, unlinkDealId]);

  useEffect(() => {
    setCurrentDeals((bitrixDeal?.placement?.bitrixDeals as any) || []);
  }, [bitrixDeal]);

  const sendPlacementData = useCallback(async (deals: IBitrixDeal[], isRemove: boolean, bitrixDealId: string | undefined, updatePlacementId: string | undefined) => {
    if (!bitrixDealId || !crmData) return;
    // Loading UI (the sidebar skeleton) is owned by the parent via onLoading.
    onLoading?.(true);

    try {
      const res = await patchBitrixDeal(
        jwt,
        bitrixDealId,
        {
          placement: isRemove ? null : `/api/placements/${updatePlacementId}`,
        },
      );
      if (!res?.ok || !res.data) {
        // Backend enforcement (403 "Field write access denied") — leave the
        // current state untouched and surface ALL accumulated reasons.
        const body = res?.data as any;
        const violation = body?.violations?.[0];
        const reasonMessages: string[] = (Array.isArray(violation?.blockActionReasons)
          ? violation.blockActionReasons : [])
          .map((reason: any) => reason?.message)
          .filter(Boolean);
        setPatchError(reasonMessages.join(' ')
          || violation?.detail
          || body?.error // legacy UnlinkDealController shape
          || body?.['hydra:description']
          || 'The change was rejected by the server.');
        // eslint-disable-next-line no-console
        console.warn('[bitrix widget] deal PATCH failed', res?.status, res?.problem, body);
        return;
      }
      const deal = res.data;

      // Keep the store copy (and its @accessRules) fresh, but never let an
      // "other" deal payload overwrite the current deal / binding flag.
      const isCurrentDeal = !!bitrixDeal && String(bitrixDealId) === String(bitrixDeal.id);
      if (isCurrentDeal) {
        dispatch(ActionsCross.setBitrixDeal(deal as IBitrixDeal));
        dispatch(AppActions.setBindingAppartment(!isRemove));
      }

      if (updatePlacementId) {
        // Raw fetch, NOT the sidebar's getPlacementDeals: on unlink the affected
        // unit may differ from the selected one, and the sidebar's hook would
        // overwrite its placementDeal (and flash the skeleton) with foreign data.
        const placementRes = await getBitrixPlacementDeals(jwt, updatePlacementId);

        dispatch(ActionsView2d.setIsRerender());
        // New array reference: pushing into the incoming array and passing the
        // same reference into setCurrentDeals would skip the re-render.
        const nextDeals = isRemove ? deals : [...deals, deal];
        if (placementRes?.ok && placementRes.data) {
          const newPlacementData = placementRes.data;
          // A payload-shape mismatch throwing inside one reducer must not silently
          // skip the remaining row updates (the grid deal marker feeds off them).
          try {
            dispatch(Actions.updatePlacement({
              data: newPlacementData,
              lang: currentLang,
            }));
            dispatch(ActionsFilteredData.updatePlacement({
              data: newPlacementData,
              lang: currentLang,
            }));
            dispatch(ActionsView2d.updatePlacement({
              data: newPlacementData,
              lang: currentLang,
            }));
            dispatch(SpinnerActions.spinnerDataEditPlacement({
              // The affected unit IS the one we just refreshed.
              id: updatePlacementId,
              crm: crmData.crm,
              data: newPlacementData,
              lang: currentLang,
            }));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[bitrix widget] placement row update failed', err);
          }
          // Sync this instance's unit data only when the affected unit IS its
          // own (attach, or unlink while focused on the linked unit).
          if (String(updatePlacementId) === String(unitId)) {
            setPlacementDeal(newPlacementData);
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('[bitrix widget] placement refresh failed', placementRes?.problem);
        }
        setCurrentDeals(nextDeals);
      }
    } finally {
      setUnlinkDealId('');
      setUndoProgress(0);
      onLoading?.(false);
    }
  }, [crmData, jwt, currentDeals, bitrixDeal, currentLang, unitId, setPlacementDeal, onLoading]);

  const handleAddDeal = (dealId: string, updatePlacement: string) => {
    sendPlacementData(currentDeals, false, dealId, updatePlacement);
  };

  const handleRemoveDeal = (selectedDeal: any = 0) => {
    const current = selectedDeal;

    const filtered: IBitrixDeal[] = currentDeals.reduce((acc: IBitrixDeal[], item: IBitrixDeal) => {
      if (item.id !== current) {
        acc.push(item);
      }
      return acc;
    }, []);

    // The grid row that changes on unlink belongs to the unit the deal was
    // LINKED TO — not necessarily the selected one (unlink is available while
    // another unit is focused). Refreshing the selected row instead left a
    // stale "linked deal" marker on the previously linked cell forever.
    const removedDeal = (bitrixDeal && String(bitrixDeal.id) === String(current) ? bitrixDeal : undefined)
      || currentDeals.find((item) => String(item.id) === String(current));
    const linkedPlacementId = extractPlacementId(removedDeal?.placement) || placementDeal?.id;

    sendPlacementData(filtered, true, current, linkedPlacementId);
  };

  const handleConnectBtn = () => {
    if (bitrixDeal && placementDeal) {
      handleAddDeal(bitrixDeal.id, placementDeal.id);
    }
  };

  const onUnlink = (dealId: string) => {
    // Reset the progress left over from a previous toast: a stale value of 100
    // would fire the unlink effect immediately, with no undo window at all.
    setUndoProgress(0);
    setUnlinkDealId(dealId);
  };

  const onUndo = () => {
    setUndoProgress(0);
    setUnlinkDealId('');
  };

  if (!crmData) {
    return null;
  }

  // Self-fetch contexts (Floors/3D popovers) have no sidebar skeleton above
  // them — show the deal-section skeleton while the unit request is in flight,
  // mirroring the dashboard sidebar behavior. Managed mode is covered by the
  // sidebar's own ApartmentSkeleton.
  if (!isPlacementDealManaged && ownPlacementDeal.isLoading) {
    return (
      <Box display="flex" flexDirection="column" gap="8px">
        <Skeleton variant="rectangular" width="100%" height={40} style={{ borderRadius: '12px' }} />
        <Skeleton variant="rectangular" width="100%" height={120} style={{ borderRadius: '12px' }} />
      </Box>
    );
  }

  return (
    <>
      {isPlacementReady && isUnitCanBeLinked && (
        <DealButton text="Link to current deal" onClick={handleConnectBtn} />
      )}
      {/* Blocked notices are mutually exclusive — exactly one renders, priority:
          1. unit status forbids linking (a deal-side permission is irrelevant when
             the unit itself can't be linked);
          2. free deal, but attaching is blocked for this user (user_isnt_responsible);
          3. fail-closed full lock (all_blocked = access_restricted/denied) —
             a neutral technical notice, works for linked AND free deals;
          4. unlink blocked by rules (reasons accumulate: responsibility / policy);
          5. deal already linked to another unit (no locks — just unlink first).
          A deal linked to THIS unit renders the green LinkedToDealButton instead. */}
      {isPlacementReady && !isDealHasCurrentUnit && isUnitCannotBeLinkedByStatus && placementDeal?.placementStatus?.crosstableName?.[currentLang] && (
        <BlockedButton label="Unit can’t be linked">
          <div className="label">
            Units in
            {' '}
            <span className="boldLabel">{placementDeal.placementStatus.crosstableName[currentLang]}</span>
            {' '}
            status can’t be linked to deals
          </div>
        </BlockedButton>
      )}
      {isPlacementReady && !isDealHasCurrentUnit && !isDealHasCurrentUnitOnFloors && !isUnitCannotBeLinkedByStatus
        && isLinkingNotEnabledForUser && (
        <BlockedButton label="Unit can’t be linked">
          <div className="label">
            {isLinkingBlockedByResponsibility
              ? 'Another user is responsible for this deal. You don’t have permission to link units to it.'
              : 'Linking units to deals isn’t enabled.'}
          </div>
        </BlockedButton>
      )}
      {isPlacementReady && !isDealHasCurrentUnit && !isDealHasCurrentUnitOnFloors && !isUnitCannotBeLinkedByStatus
        && isBitrixDealAccessRestricted && (
        <BlockedButton label="Access restricted">
          <div className="label">
            {bitrixDealAccessRestrictedMessage || 'Access restricted.'}
          </div>
        </BlockedButton>
      )}
      {isPlacementReady && !isDealHasCurrentUnit && !isDealHasCurrentUnitOnFloors && !isUnitCannotBeLinkedByStatus
        && !isBitrixDealAccessRestricted
        && isBitrixDealUnlinkBlocked && bitrixDeal?.placement?.numeration?.[currentLang] && (
        <BlockedButton label="Other unit linked to deal">
          <div className="label">
            <span className="boldLabel">
              #
              { bitrixDeal.placement.numeration[currentLang] }
            </span>
            {' '}
            is linked to this deal. Your current permissions do not allow you to unlink it to attach a different unit.
          </div>
        </BlockedButton>
      )}
      {isPlacementReady && !isDealHasCurrentUnit && !isDealHasCurrentUnitOnFloors && !isUnitCannotBeLinkedByStatus
        && !isBitrixDealAccessRestricted && !isBitrixDealUnlinkBlocked
        && bitrixDeal?.placement?.numeration?.[currentLang] && (
        <BlockedButton label="Other unit linked to deal">
          <div className="label">
            <span className="boldLabel">
              #
              { bitrixDeal.placement.numeration[currentLang] }
            </span>
            {' '}
            is linked to current deal. Unlink it to attach this unit.
          </div>
        </BlockedButton>
      )}
      {(isDealHasCurrentUnit || isDealHasCurrentUnitOnFloors) && (
        <LinkedToDealButton />
      )}
      {!!unlinkDealId.length && undoProgress !== 100 && (<ApartmentSidebarUndo placementDeal={placementDeal} onProgress={setUndoProgress} onUndo={onUndo} />)}
      <DealDetailsWrapper
        bitrixDeal={bitrixDeal}
        placementDeal={placementDeal}
        onUnlink={onUnlink}
      />
      <Snackbar
        open={!!patchError}
        autoHideDuration={6000}
        onClose={() => setPatchError(null)}
      >
        <Alert severity="error" onClose={() => setPatchError(null)}>
          {patchError}
        </Alert>
      </Snackbar>
    </>
  );
});
